import React, { useState, Fragment, useEffect } from 'react';
import { css } from 'twin.macro';
import { Modal, Button, Row, Col, ListGroup } from 'react-bootstrap';
import { BsEye } from 'react-icons/bs';
import ImagesMessage from '@/components/Features/Common/Messages/ImagesMessage';
import { getStatusBadge } from './utils';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { fetchWrapper } from '@/_helpers';
import { toast } from 'react-toastify';
import { VscLoading } from 'react-icons/vsc';
import { formatDateShort } from '.';

const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
    }).format(amount);
};

const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const ExpenseDetailsModal = ({ expenseObj, handleUpdateExpense }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [expense, setExpense] = useState(expenseObj);
    const handleOpen = () => setIsOpen(true);
    const handleClose = () => setIsOpen(false);

    const loadExpenseDetails = async () => {
        try {
            if (!expenseObj?._id) {
                return;
            }
            setIsBusy(true);
            const expenseId = expenseObj._id;
            const response = await fetchWrapper.get(`/expenses/${expenseId}`);
            setExpense(response);
            handleUpdateExpense(response);
        } catch (error) {
            if (error?.name === 'AbortError') return;
            toast.error(error.message || 'Failed to fetch expense details');
            handleClose();
        } finally {
            setIsBusy(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadExpenseDetails();
        }
    }, [isOpen]);
    if (!expenseObj?._id) return null;

    return (
        <Fragment>
            <BootstrapToolTip text="View">
                <BsEye className="icon view" onClick={handleOpen} />
            </BootstrapToolTip>
            <Modal show={isOpen} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Expense Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {expense?.files && expense?.files.length > 0 ? (
                        <Fragment>
                            <ImagesMessage
                                images={expense?.files || []}
                                displayMessage={''}
                                from="current-user"
                                css={css`
                                    color: white !important;
                                `}
                                teamDetails={true}
                                transcriptions={[]}
                            />
                            {isBusy && (
                                <div className="mb-4">
                                    <VscLoading className="mr-2 spin" />
                                    Loading Receipt Image...
                                </div>
                            )}
                        </Fragment>
                    ) : (
                        <div style={{ color: '#999', fontStyle: 'italic' }}>No files attached</div>
                    )}
                    <ListGroup
                        css={css`
                            .list-group-item {
                                display: flex;
                                justify-content: space-between;
                            }
                        `}
                    >
                        <ListGroup.Item>
                            <b>Expense ID:</b>#{expense._id}
                        </ListGroup.Item>
                        <ListGroup.Item>
                            <b>Agency Name:</b>
                            {expense.providerData?.agencyName}
                        </ListGroup.Item>
                        <ListGroup.Item>
                            <b>Employee:</b>
                            {expense.employeeData?.user?.name}
                        </ListGroup.Item>

                        <ListGroup.Item>
                            <b>Vendor/Merchant:</b> {expense.vendor}
                        </ListGroup.Item>
                        <ListGroup.Item>
                            <b>Amount:</b> {formatCurrency(expense.amount, expense.currency)}
                        </ListGroup.Item>
                        <ListGroup.Item>
                            <b>Category:</b> {expense.category}
                        </ListGroup.Item>
                        <ListGroup.Item>
                            <b>Payment Method:</b>
                            {expense?.paymentMethod}
                        </ListGroup.Item>
                        <ListGroup.Item>
                            <b>Receipt Date:</b> {formatDateShort(expense.receiptDate)}
                        </ListGroup.Item>
                        <ListGroup.Item>
                            <b>Status:</b> {getStatusBadge(expense.status)}
                        </ListGroup.Item>
                        {expense?.approvedBy && (
                            <Fragment>
                                <ListGroup.Item>
                                    <b>Approved By:</b> {(expense.approvedByData || {})?.name || '-'}
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <b>Approved At:</b> {formatDate(expense.approvedAt)}
                                </ListGroup.Item>
                            </Fragment>
                        )}

                        {expense?.rejectedBy && (
                            <Fragment>
                                <ListGroup.Item>
                                    <b>Rejected By:</b> {(expense.rejectedByData || {})?.name || '-'}
                                </ListGroup.Item>

                                <ListGroup.Item>
                                    <b>Rejected At:</b> {formatDate(expense.rejectedAt)}
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <b>Rejection Reason:</b> {expense?.rejectionReason}
                                </ListGroup.Item>
                            </Fragment>
                        )}
                    </ListGroup>
                    <Row>
                        <Col md={8}>
                            {expense.notes && (
                                <div className="info-row">
                                    <div className="info-label">Notes</div>
                                    <div className="info-value">{expense.notes}</div>
                                </div>
                            )}
                        </Col>

                        <Col md={4}></Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

export default ExpenseDetailsModal;
